/**
 * Impresiones de anuncios viewables vía Google Publisher Tag (GPT / Google Ad Manager).
 *
 * Cada evento `impressionViewable` (Active View: ≥50% de píxeles ≥1s en viewport)
 * se emite como un evento DISCRETO `ad_impression` con su propio eventId. Así cada
 * impresión es un HECHO GUARDADO Y DEDUPLICADO en el stream, auditable en el desglose:
 * el IPVPS es el RECUENTO de estas filas, no un delta opaco que viajaba en un heartbeat
 * (inauditable, e inflable si un lote se reentregaba). Un reintento del lote se deduplica
 * por eventId → el contador no se puede inflar.
 *
 * `googletag.cmd.push` es seguro aunque `gpt.js` cargue tarde o tras el consentimiento
 * (GPT drena la cola en cuanto está listo). Si nunca hay GPT (p. ej. AdSense puro sin
 * GAM), no se emite nada (degradación silenciosa). Cada impresión refrescada (auto-refresh
 * de slot) dispara el evento de nuevo → otra fila. PERO solo se CUENTA con presencia:
 * `bindAdsEmit` recibe un gate (makeAdImpressionGate) que descarta la impresión si la pestaña
 * está oculta o el usuario lleva >45 min sin interactuar (tab abandonado). Sin el gate, un tab
 * abandonado horas con auto-refresh inflaba el IPVPS con impresiones sin nadie delante (un caso
 * real: 1 sola sesión = 13,6 h de cola muerta = ~10% de las impresiones del proyecto).
 *
 * IMPORTANTE (timing): `startAds()` se llama lo antes posible (al ejecutarse el SDK, antes
 * del `boot()` async), no tras el fetch de config, para no perder la 1ª impresión
 * above-the-fold en cargas rápidas. Como `emit` aún no existe en ese momento, las
 * impresiones tempranas se BUFFERIZAN y se vuelcan en cuanto `bindAdsEmit()` conecta el
 * emit real (durante el boot). Registrar el listener es pasivo (un push a la cola de GPT):
 * no carga GPT antes ni bloquea el render.
 */

import type { Context } from "../context.js";

type EmitFn = (type: string, props?: Record<string, unknown>) => void;
type GptSlot = { getAdUnitPath?: () => string; getSlotElementId?: () => string };
type ImpressionEvent = { slot?: GptSlot };
type GptPubads = { addEventListener: (event: string, cb: (e?: ImpressionEvent) => void) => void };
type Googletag = { cmd: Array<() => void>; pubads?: () => GptPubads };

/** Devuelve true si la impresión debe CONTAR (pestaña visible + usuario presente). */
type AdsGate = () => boolean;

/** Inactividad (ms) tras la cual una impresión deja de contar como "viewable con presencia".
 *  45 min: validado contra datos reales (1 SPA + 2 MPA) — ningún tráfico legítimo supera ~30 min
 *  sin interacción; solo lo cruzan los tabs abandonados (justo lo que queremos descartar). */
export const AD_IMPRESSION_MAX_IDLE_MS = 45 * 60 * 1000;

let started = false;
let emitFn: EmitFn | null = null;
let gateFn: AdsGate = () => true;
const buffered: Array<Record<string, unknown>> = [];

/** Emite la impresión como evento. Pre-boot (emit aún sin conectar) la bufferiza. Una vez
 *  conectado, aplica el gate: si NO pasa (oculta / idle largo) se DESCARTA — el descarte NO se
 *  bufferiza (bufferizarlo reintroduciría el inflado al volver a estar visible). */
function recordImpression(props: Record<string, unknown>): void {
  if (emitFn) {
    if (gateFn()) emitFn("ad_impression", props);
  } else {
    buffered.push(props);
  }
}

/** Gate de impresión: cuenta solo si la pestaña está visible Y el usuario no lleva más de
 *  `maxIdleMs` sin interactuar. `idleMs` (lo mantiene idle.ts) se resetea con CUALQUIER input
 *  —incluido mousemove—, así que un presente que ve un vídeo sin scrollear NO se gatea; solo el
 *  ausente real (ni un mousemove en 45 min) lo cruza. `env` se inyecta en tests. */
export function makeAdImpressionGate(
  ctx: Context,
  maxIdleMs: number = AD_IMPRESSION_MAX_IDLE_MS,
  env?: { isHidden: () => boolean; getIdleMs: () => number }
): AdsGate {
  const isHidden = env?.isHidden ?? (() => document.visibilityState !== "visible");
  const getIdleMs = env?.getIdleMs ?? (() => ctx.state.idleMs);
  return () => !isHidden() && getIdleMs() <= maxIdleMs;
}

/** Conecta el emit real (tras el boot) + el gate, y vuelca las impresiones tempranas
 *  bufferizadas SIN gatear (las above-the-fold iniciales, capturadas con la pestaña visible y
 *  el usuario recién llegado, son legítimas). */
export function bindAdsEmit(emit: EmitFn, gate?: AdsGate): void {
  emitFn = emit;
  if (gate) gateFn = gate;
  for (const p of buffered.splice(0)) emit("ad_impression", p);
}

/** Engancha el listener de impresiones viewables. Idempotente: solo actúa una vez. */
export function startAds(): void {
  if (started) return;
  started = true;
  try {
    const w = window as unknown as { googletag?: Googletag };
    const gt: Googletag = (w.googletag = w.googletag || { cmd: [] });
    gt.cmd.push(() => {
      try {
        const pubads = w.googletag?.pubads?.();
        if (!pubads) return;
        pubads.addEventListener("impressionViewable", (e) => {
          // Etiqueta de slot para auditar la impresión (ruta del ad unit o id del div).
          // Defensivo: si el getter no está, la impresión cuenta igual, sin etiqueta.
          let slot: string | undefined;
          try {
            slot = e?.slot?.getAdUnitPath?.() || e?.slot?.getSlotElementId?.();
          } catch {
            /* API de slot inesperada: ignoramos la etiqueta */
          }
          recordImpression(slot ? { slot } : {});
        });
      } catch {
        /* API de GPT inesperada: ignoramos */
      }
    });
  } catch {
    /* entorno sin window: degradación silenciosa */
  }
}
